/**
 * Voice Activity Detection (VAD) utility
 * Monitors audio stream to detect when user starts/stops speaking
 */

export interface VADConfig {
  silenceThreshold?: number; // Volume threshold to detect silence (default: 5)
  silenceDuration?: number; // Duration of silence before triggering callback in ms (default: 1500)
  checkInterval?: number; // How often to check audio levels in ms (default: 100)
}

export interface VADCallbacks {
  onSpeechStart?: () => void; // Called when user starts speaking
  onSpeechEnd?: () => void; // Called when user stops speaking
  onSilenceDetected?: () => void; // Called after silence duration is reached
  onAudioLevel?: (level: number) => void; // Called with current audio level for debugging
}

const DEFAULT_CONFIG: Required<VADConfig> = {
  silenceThreshold: 5,
  silenceDuration: 1500,
  checkInterval: 100,
};

export class VoiceActivityDetector {
  private analyser: AnalyserNode | null = null;
  private intervalId: NodeJS.Timeout | null = null;
  private silenceTimerId: NodeJS.Timeout | null = null;
  private isSpeaking = false;
  private dataArray: Uint8Array | null = null;

  private config: Required<VADConfig>;
  private callbacks: VADCallbacks;

  constructor(config: VADConfig = {}, callbacks: VADCallbacks = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.callbacks = callbacks;
  }

  /**
   * Start monitoring audio stream for voice activity
   * @param audioContext - The audio context
   * @param stream - The media stream to monitor
   * @param existingAnalyser - Optional existing analyser node to reuse
   */
  start(audioContext: AudioContext, stream: MediaStream, existingAnalyser?: AnalyserNode): void {
    if (this.intervalId) {
      console.warn('VAD already started');
      return;
    }

    // Use existing analyser or create new one
    if (existingAnalyser) {
      this.analyser = existingAnalyser;
    } else {
      // Set up audio analysis
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      this.analyser = analyser;
    }

    this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.isSpeaking = false;

    // Start monitoring
    this.intervalId = setInterval(() => {
      this.checkAudioLevel();
    }, this.config.checkInterval);
  }

  /**
   * Stop monitoring and clean up resources
   */
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.silenceTimerId) {
      clearTimeout(this.silenceTimerId);
      this.silenceTimerId = null;
    }

    this.analyser = null;
    this.dataArray = null;
    this.isSpeaking = false;
  }

  /**
   * Check current audio level and detect speech/silence
   */
  private checkAudioLevel(): void {
    if (!this.analyser || !this.dataArray) return;

    this.analyser.getByteTimeDomainData(this.dataArray);

    // Calculate average volume level
    let sum = 0;
    for (let i = 0; i < this.dataArray.length; i++) {
      sum += Math.abs(this.dataArray[i] - 128);
    }
    const average = sum / this.dataArray.length;

    // Report current level for debugging
    this.callbacks.onAudioLevel?.(average);

    const isSpeaking = average > this.config.silenceThreshold;

    // User started speaking
    if (isSpeaking && !this.isSpeaking) {
      this.isSpeaking = true;
      this.callbacks.onSpeechStart?.();

      // Clear any pending silence timer
      if (this.silenceTimerId) {
        clearTimeout(this.silenceTimerId);
        this.silenceTimerId = null;
      }
    }

    // User might have stopped speaking - start debounce timer if not already started
    if (!isSpeaking && this.isSpeaking && !this.silenceTimerId) {
      // Start debounce timer - only fire onSpeechEnd after sustained silence
      this.silenceTimerId = setTimeout(() => {
        this.isSpeaking = false;
        this.callbacks.onSpeechEnd?.();
        this.callbacks.onSilenceDetected?.();
        this.silenceTimerId = null;
      }, this.config.silenceDuration);
    }

    // User started speaking again - cancel the silence timer
    if (isSpeaking && this.isSpeaking && this.silenceTimerId) {
      clearTimeout(this.silenceTimerId);
      this.silenceTimerId = null;
    }
  }

  /**
   * Check if currently speaking
   */
  isSpeakingNow(): boolean {
    return this.isSpeaking;
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<VADConfig>): void {
    this.config = { ...this.config, ...config };
  }
}
